import React, { useState, useEffect } from 'react'
import { api } from '../../services'

const DEFAULT_LOW_BATTERY_THRESHOLD = 3.0
const DEFAULT_NO_POWER_THRESHOLD = 0.5
const DEFAULT_OFFLINE_MINUTES = 360
const DEFAULT_OFFLINE_MODE = 'scheduled'
const STATUS_MESSAGE_DURATION_MS = 4000

export function AccountAlertSettingsSection() {
  // Battery alert state
  const [batteryEnabled, setBatteryEnabled] = useState(false)
  const [lowThreshold, setLowThreshold] = useState(DEFAULT_LOW_BATTERY_THRESHOLD)
  const [noPowerThreshold, setNoPowerThreshold] = useState(DEFAULT_NO_POWER_THRESHOLD)

  // Offline alert state
  const [offlineEnabled, setOfflineEnabled] = useState(false)
  const [offlineMinutes, setOfflineMinutes] = useState(DEFAULT_OFFLINE_MINUTES)
  const [offlineMode, setOfflineMode] = useState(DEFAULT_OFFLINE_MODE)

  // Save state
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')

  // Test alert state
  const [testEmail, setTestEmail] = useState('')
  const [testPhone, setTestPhone] = useState('')
  const [testingEmail, setTestingEmail] = useState(false)
  const [testingSms, setTestingSms] = useState(false)
  const [testMessage, setTestMessage] = useState('')

  useEffect(() => {
    api.getAccountAlertSettings()
      .then((res: any) => {
        if (res?.data) {
          setBatteryEnabled(res.data.batteryAlertsEnabled ?? false)
          setLowThreshold(res.data.lowBatteryThreshold ?? DEFAULT_LOW_BATTERY_THRESHOLD)
          setNoPowerThreshold(res.data.noPowerThreshold ?? DEFAULT_NO_POWER_THRESHOLD)
          setOfflineEnabled(res.data.offlineAlertsEnabled ?? false)
          setOfflineMinutes(res.data.offlineThresholdMinutes ?? DEFAULT_OFFLINE_MINUTES)
          setOfflineMode(res.data.offlineModesMonitored ?? DEFAULT_OFFLINE_MODE)
        }
      })
      .catch(() => {})
  }, [])

  async function handleSave() {
    setSaving(true)
    setSaveMessage('')
    try {
      await api.updateAccountAlertSettings({
        batteryAlertsEnabled: batteryEnabled,
        lowBatteryThreshold: lowThreshold,
        noPowerThreshold: noPowerThreshold,
        offlineAlertsEnabled: offlineEnabled,
        offlineThresholdMinutes: offlineMinutes,
        offlineModesMonitored: offlineMode,
      })
      setSaveMessage('Settings saved successfully')
      setTimeout(() => setSaveMessage(''), STATUS_MESSAGE_DURATION_MS)
    } catch {
      setSaveMessage('Failed to save settings')
    }
    setSaving(false)
  }

  async function handleTestEmail() {
    if (!testEmail) return
    setTestingEmail(true)
    setTestMessage('')
    try {
      await api.sendTestAlert({ type: 'email', contact: testEmail })
      setTestMessage('Test email sent successfully')
      setTimeout(() => setTestMessage(''), STATUS_MESSAGE_DURATION_MS)
    } catch {
      setTestMessage('Failed to send test email')
    }
    setTestingEmail(false)
  }

  async function handleTestSms() {
    if (!testPhone) return
    setTestingSms(true)
    setTestMessage('')
    try {
      await api.sendTestAlert({ type: 'sms', contact: testPhone })
      setTestMessage('Test SMS sent successfully')
      setTimeout(() => setTestMessage(''), STATUS_MESSAGE_DURATION_MS)
    } catch {
      setTestMessage('Failed to send test SMS')
    }
    setTestingSms(false)
  }

  return (
    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
      <h3 className="text-sm font-medium text-gray-900 mb-4">Account Alert Settings</h3>

      <p className="text-xs text-gray-600 mb-4">
        Configure account-level alert settings for battery and offline monitoring.
        These settings apply to all devices on your account.
      </p>

      {/* Battery Alerts */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-800 mb-3">Battery Alerts</h4>
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Setting</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Value</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">Enabled</td>
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={batteryEnabled}
                    onChange={(e) => setBatteryEnabled(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">Enable battery voltage alerts for all devices</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">Low Battery Threshold</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={lowThreshold}
                      onChange={(e) => setLowThreshold(parseFloat(e.target.value) || 0)}
                      disabled={!batteryEnabled}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className="text-xs text-gray-500">V</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">Alert when battery drops below this voltage</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">No Power Threshold</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={noPowerThreshold}
                      onChange={(e) => setNoPowerThreshold(parseFloat(e.target.value) || 0)}
                      disabled={!batteryEnabled}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className="text-xs text-gray-500">V</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">Critical alert when battery drops below this voltage</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Offline Alerts */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-800 mb-3">Offline Alerts</h4>
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Setting</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Value</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">Enabled</td>
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={offlineEnabled}
                    onChange={(e) => setOfflineEnabled(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">Enable offline detection alerts for all devices</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">Offline Threshold</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="1"
                      min="1"
                      value={offlineMinutes}
                      onChange={(e) => setOfflineMinutes(parseInt(e.target.value) || 0)}
                      disabled={!offlineEnabled}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className="text-xs text-gray-500">minutes</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">Alert when device has not reported for this many minutes</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">Offline Mode</td>
                <td className="px-6 py-4">
                  <select
                    value={offlineMode}
                    onChange={(e) => setOfflineMode(e.target.value)}
                    disabled={!offlineEnabled}
                    className="w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="scheduled">Scheduled Mode Only</option>
                    <option value="config">Config Mode Only</option>
                    <option value="all">All Modes</option>
                  </select>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">Which message modes to monitor for offline detection</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Save Button */}
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {saveMessage && (
          <span className={`text-sm ${saveMessage.includes('successfully') ? 'text-green-600' : 'text-red-600'}`}>
            {saveMessage}
          </span>
        )}
      </div>

      {/* Test Alert Section */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Test Alerts</h4>
        <p className="text-xs text-blue-800 mb-4">
          Send a test alert to verify your notification contacts are working correctly.
          This will send a test message through the configured alert system.
        </p>

        <div className="space-y-3">
          {/* Test Email */}
          <div className="flex items-center gap-3">
            <input
              type="email"
              placeholder="Email address"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="w-64 px-3 py-2 border border-blue-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
            <button
              onClick={handleTestEmail}
              disabled={testingEmail || !testEmail}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {testingEmail ? 'Sending...' : 'Send Test Email'}
            </button>
          </div>

          {/* Test SMS */}
          <div className="flex items-center gap-3">
            <input
              type="tel"
              placeholder="Phone number"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              className="w-64 px-3 py-2 border border-blue-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
            <button
              onClick={handleTestSms}
              disabled={testingSms || !testPhone}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {testingSms ? 'Sending...' : 'Send Test SMS'}
            </button>
          </div>

          {/* Test Result Message */}
          {testMessage && (
            <p className={`text-sm font-medium ${testMessage.includes('successfully') ? 'text-green-700' : 'text-red-700'}`}>
              {testMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
